import React, { useState, useEffect } from "react";
import "./App.css";
import { db, auth } from './utils/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, onSnapshot, updateDoc, arrayUnion, arrayRemove, getDocs, limit } from 'firebase/firestore';
import { QuoteProvider } from './components/Quote/QuoteContext';
import QuotePopup from './components/Quote/Quote';
import { UserSchemeProvider } from './components/UserSchemeContext';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhyChefco from "./components/pages/WhyChefco";
import Departments from "./components/pages/Departments";
import QuickOrders from "./components/pages/QuickOrders";
import Products from "./components/pages/Products";
import ProductDetails from "./components/pages/ProductDetails";
import CartModal from "./components/CartModal";
import AuthModal from "./components/AuthModal";
import HomePage from "./components/pages/HomePage";
import FlyerDeals from "./components/pages/FlyerDeals";
import MyLists from "./components/pages/MyLists";
import Checkout from "./components/pages/Checkout";
import OrderSuccess from "./components/pages/OrderSuccess";
import Profile from "./components/pages/Profile";
import Cart from "./components/pages/Cart";
import Inventory from "./components/pages/Inventory";
import { toast, Toaster } from 'sonner';
import LoadingScreen from './components/LoadingScreen';
import { X } from 'lucide-react';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedSectionName, setSelectedSectionName] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [dbLoaded, setDbLoaded] = useState(false);
  const [userDocKey, setUserDocKey] = useState(null);

  const [isAppReady, setIsAppReady] = useState(false);
  const [showAppBanner, setShowAppBanner] = useState(() => {
    return localStorage.getItem('dismissedAppBanner') !== 'true';
  });
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileDevice(window.innerWidth <= 768 || /Mobi|Android|iPhone/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  useEffect(() => {
    setIsAppReady(true);
  }, []);

  // ── Per-user cart ─────────────────────────────────────────────────────────
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // ── Per-user myList ───────────────────────────────────────────────────────
  const [myList, setMyList] = useState([]);
  const [isAuthOpen, setIsAuthOpen] = useState(false);

  // Real-time wishlist listener based on wishlistIds field on product documents
  useEffect(() => {
    let unsubscribeWishlist = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (unsubscribeWishlist) {
        unsubscribeWishlist();
        unsubscribeWishlist = null;
      }

      if (user) {
        const q = query(
          collection(db, "products"),
          where("wishlistIds", "array-contains", user.uid)
        );
        unsubscribeWishlist = onSnapshot(q, (snapshot) => {
          const likedIds = snapshot.docs.map(doc => doc.id);
          setMyList(likedIds);
        }, (error) => {
          console.error("Error listening to user wishlist:", error);
        });
      } else {
        setMyList([]);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeWishlist) unsubscribeWishlist();
    };
  }, []);

  // Listen for open-auth events to open the auth modal
  useEffect(() => {
    const handleOpenAuth = () => setIsAuthOpen(true);
    window.addEventListener('open-auth', handleOpenAuth);
    return () => window.removeEventListener('open-auth', handleOpenAuth);
  }, []);

  // Listen for Firebase Auth changes and fetch user data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        setDbLoaded(false);
        try {
          let resolvedKey = user.uid;
          let userDocRef = doc(db, "users", resolvedKey);
          let docSnap = await getDoc(userDocRef);
          if (!docSnap.exists()) {
            resolvedKey = user.email.toLowerCase();
            userDocRef = doc(db, "users", resolvedKey);
            docSnap = await getDoc(userDocRef);
          }
          setUserDocKey(docSnap.exists() ? resolvedKey : user.email.toLowerCase());

          if (docSnap.exists()) {
            const data = docSnap.data();

            // Synced user session data to localStorage
            const sessionUser = {
              uid: user.uid,
              email: user.email,
              fullName: data.fullName || data.name || user.displayName || user.email,
              isLoggedIn: true,
              ...data
            };
            localStorage.setItem('user', JSON.stringify(sessionUser));
            window.dispatchEvent(new Event('user-login'));

            // Load and map cart items from carts collection
            const cartDocRef = doc(db, "carts", resolvedKey);
            const cartSnap = await getDoc(cartDocRef);
            let firestoreCart = [];
            if (cartSnap.exists()) {
              firestoreCart = cartSnap.data().items || [];
            }
            const mappedCart = await Promise.all(firestoreCart.map(async (item) => {
              const itemId = item.id;
              try {
                const productDocRef = doc(db, 'products', itemId);
                const productSnap = await getDoc(productDocRef);
                if (productSnap.exists()) {
                  const data = productSnap.data();
                  const code = data.productCodeLetters && data.productCode
                    ? `${data.productCodeLetters}${data.productCode}`
                    : (data.code || '');
                  const product = { id: productSnap.id, title: data.productName || data.title || '', code, ...data };
                  return { product, quantity: Number(item.cartQuantity || item.cartQty || item.quantity) || 1 };
                }
              } catch (err) {
                console.error("Error fetching cart item product:", err);
              }
              return { product: { id: itemId, title: 'Unknown Product' }, quantity: Number(item.cartQuantity || item.cartQty || item.quantity) || 1 };
            }));
            setCart(mappedCart);

            // Load wishlist & migrate old items to wishlistIds array on product documents
            const oldMyList = data.myList || [];
            setMyList(oldMyList);
            if (oldMyList.length > 0) {
              const uid = user.uid;
              (async () => {
                for (const itemKey of oldMyList) {
                  if (!itemKey) continue;
                  let matchingProduct = null;
                  try {
                    const prodDocRef = doc(db, "products", itemKey);
                    const prodDocSnap = await getDoc(prodDocRef);
                    if (prodDocSnap.exists()) {
                      matchingProduct = { id: prodDocSnap.id };
                    } else {
                      const qCode = query(collection(db, "products"), where("code", "==", itemKey), limit(1));
                      const snapCode = await getDocs(qCode);
                      if (!snapCode.empty) {
                        matchingProduct = { id: snapCode.docs[0].id };
                      } else {
                        const qTitle = query(collection(db, "products"), where("productName", "==", itemKey), limit(1));
                        const snapTitle = await getDocs(qTitle);
                        if (!snapTitle.empty) {
                          matchingProduct = { id: snapTitle.docs[0].id };
                        }
                      }
                    }
                  } catch (err) {
                    console.error("Migration product lookup error:", err);
                  }

                  if (matchingProduct) {
                    const productRef = doc(db, "products", matchingProduct.id);
                    try {
                      await updateDoc(productRef, {
                        wishlistIds: arrayUnion(uid)
                      });
                    } catch (migrationError) {
                      console.error(`Error migrating product ${matchingProduct.id} to wishlistIds:`, migrationError);
                    }
                  }
                }
              })();
            }
          } else {
            const sessionUser = {
              uid: user.uid,
              email: user.email,
              fullName: user.displayName || user.email,
              isLoggedIn: true
            };
            localStorage.setItem('user', JSON.stringify(sessionUser));
            window.dispatchEvent(new Event('user-login'));

            setCart([]);
            setMyList([]);
          }
        } catch (e) {
          console.error("Failed to load user data from firestore", e);
        } finally {
          setDbLoaded(true);
        }
      } else {
        setCart([]);
        setMyList([]);
        setUserDocKey(null);
        setDbLoaded(false);
        localStorage.removeItem('user');
        window.dispatchEvent(new Event('user-login'));
      }
    });
    return () => unsubscribe();
  }, []);

  // Save/sync cart and myList to Firestore
  useEffect(() => {
    if (!currentUser || !dbLoaded || !userDocKey) return;

    const syncWithFirestore = async () => {
      try {
        const userDocRef = doc(db, "users", userDocKey);
        await setDoc(userDocRef, {
          myList: myList
        }, { merge: true });

        const cartDocRef = doc(db, "carts", userDocKey);
        await setDoc(cartDocRef, {
          items: cart.map(item => ({
            id: item.product.id || "",
            cartQuantity: item.quantity
          }))
        });
      } catch (e) {
        console.error("Failed to sync user data with firestore", e);
      }
    };

    const timer = setTimeout(syncWithFirestore, 500);
    return () => clearTimeout(timer);
  }, [cart, myList, currentUser, dbLoaded, userDocKey]);

  // add to wishlist
  const handleToggleMyList = async (productKey) => {
    if (!auth.currentUser) {
      toast.error("Please sign in to add items to your wishlist.");
      setIsAuthOpen(true);
      return;
    }

    const uid = auth.currentUser.uid;
    const productRef = doc(db, "products", productKey);
    const isLiked = myList.includes(productKey);

    try {
      if (isLiked) {
        await updateDoc(productRef, {
          wishlistIds: arrayRemove(uid)
        });
        toast.success("Removed from wishlist");
      } else {
        await updateDoc(productRef, {
          wishlistIds: arrayUnion(uid)
        });
        toast.success("Added to wishlist");
      }
    } catch (e) {
      console.error("Failed to toggle wishlist in firestore", e);
      toast.error("Failed to update wishlist");
    }
  };

  const getProductCode = (p) => {
    if (!p) return '';
    if (p.code) return p.code;
    if (p.productCodeLetters && p.productCode) return `${p.productCodeLetters}${p.productCode}`;
    return p.productCode || '';
  };

  const getProductTitle = (p) => {
    if (!p) return '';
    return p.title || p.productName || '';
  };

  const getProductId = (p) => {
    if (!p) return '';
    return p.id || p.objectID || '';
  };

  const areProductsEqual = (p1, p2) => {
    const id1 = getProductId(p1);
    const id2 = getProductId(p2);
    if (id1 && id2) return id1 === id2;

    const code1 = getProductCode(p1);
    const code2 = getProductCode(p2);
    if (code1 && code2) return code1 === code2;

    const title1 = getProductTitle(p1);
    const title2 = getProductTitle(p2);
    return !!(title1 && title2 && title1 === title2);
  };

  // add to cart
  const handleAddToCart = (product, qty) => {
    if (!auth.currentUser) {
      toast.error("Please sign in to add items to your cart.");
      setIsAuthOpen(true);
      return;
    }

    // Normalize product fields to match Firestore-loaded format
    const code = product.code ||
      (product.productCodeLetters && product.productCode
        ? `${product.productCodeLetters}${product.productCode}`
        : product.productCode || '');
    const title = product.title || product.productName || '';
    const normalizedProduct = { ...product, title, code };

    const existingItemIndex = cart.findIndex(
      (item) => areProductsEqual(item.product, normalizedProduct)
    );
    const existingQty = existingItemIndex > -1 ? cart[existingItemIndex].quantity : 0;
    const targetQty = existingQty + qty;
    const availableStock = product.stock !== undefined ? Number(product.stock) : undefined;

    if (availableStock !== undefined && !isNaN(availableStock) && targetQty > availableStock) {
      toast.error("Out of stock");
    }

    setCart((prevCart) => {
      let updatedCart;
      if (existingItemIndex > -1) {
        updatedCart = prevCart.map((item, idx) =>
          idx === existingItemIndex
            ? { ...item, quantity: item.quantity + qty }
            : item
        );
      } else {
        updatedCart = [...prevCart, { product: normalizedProduct, quantity: qty }];
      }

      return updatedCart;
    });
    toast.success("Item added to cart successfully");
  };


  const handleUpdateCartQty = (product, qty) => {
    if (!auth.currentUser) {
      toast.error("Please sign in to modify your cart.");
      setIsAuthOpen(true);
      return;
    }

    const availableStock = product.stock !== undefined ? Number(product.stock) : undefined;
    if (availableStock !== undefined && !isNaN(availableStock) && qty > availableStock) {
      toast.error("Out of stock");
    }

    setCart((prevCart) => {
      const existingItemIndex = prevCart.findIndex(
        (item) => areProductsEqual(item.product, product)
      );

      let updatedCart;
      if (existingItemIndex > -1) {
        if (qty <= 0) {
          updatedCart = prevCart.filter((_, idx) => idx !== existingItemIndex);
          toast.success("Item removed from cart");
        } else {
          updatedCart = prevCart.map((item, idx) =>
            idx === existingItemIndex ? { ...item, quantity: qty } : item
          );
        }
      } else if (qty > 0) {
        updatedCart = [...prevCart, { product, quantity: qty }];
        toast.success("Item added to cart successfully");
      } else {
        updatedCart = prevCart;
      }
      return updatedCart;
    });
  };

  const cartCount = cart.length;
  return (
    <Router>
      {!isAppReady && <LoadingScreen />}
      <UserSchemeProvider>
      <QuoteProvider cart={cart} handleUpdateCartQty={handleUpdateCartQty}>
        <ScrollToTop />
        {isMobileDevice && showAppBanner && (
          <div className="bg-[#2C3E2B] text-white text-xs font-bold px-4 py-2.5 flex items-center justify-between z-50 sticky top-0 left-0 right-0 border-b border-white/10 shadow-md print:hidden">
            <div className="flex items-center gap-2 text-left">
              <span className="text-sm flex-shrink-0">📱</span>
              <span>Get the ChefCo Mobile App for the best ordering experience!</span>
            </div>
            <div className="flex items-center gap-3 shrink-0 ml-4">
              <a href="https://example.com/download-app" target="_blank" rel="noopener noreferrer" className="bg-white text-[#2C3E2B] px-3 py-1 rounded-full font-black hover:scale-105 active:scale-95 transition-all text-[10px] no-underline">
                Download
              </a>
              <button 
                onClick={() => {
                  setShowAppBanner(false);
                  localStorage.setItem('dismissedAppBanner', 'true');
                }} 
                className="text-white hover:text-slate-200 bg-transparent border-none cursor-pointer flex items-center justify-center p-0.5" 
                aria-label="Close app banner"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* ================= NAVBAR ================= */}
        <div className="print:hidden">
          <Navbar
            cartCount={cartCount}
            myListCount={myList.length}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedSubcategory={selectedSubcategory}
            setSelectedSubcategory={setSelectedSubcategory}
            onCartClick={() => setIsCartOpen(true)}
            onAuthClick={() => setIsAuthOpen(true)}
          />
        </div>

        {/* ================= ROUTES ================= */}
        <Routes>
          <Route path="/" element={<HomePage handleAddToCart={handleAddToCart} searchQuery={searchQuery} setSearchQuery={setSearchQuery} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} selectedSubcategory={selectedSubcategory} setSelectedSubcategory={setSelectedSubcategory} selectedSectionId={selectedSectionId} setSelectedSectionId={setSelectedSectionId} selectedSectionName={selectedSectionName} setSelectedSectionName={setSelectedSectionName} myList={myList} handleToggleMyList={handleToggleMyList} cartCount={cartCount} onCartClick={() => setIsCartOpen(true)} onAuthClick={() => setIsAuthOpen(true)} />} />
          <Route path="/why" element={<WhyChefco />} />
          <Route path="/departments" element={<Departments />} />
          <Route path="/quickorder" element={<QuickOrders />} />
          <Route path='/flyerdeals' element={<FlyerDeals />} />
          <Route path='/my-lists' element={<MyLists myList={myList} handleAddToCart={handleAddToCart} handleToggleMyList={handleToggleMyList} />} />
          <Route path='/checkout' element={<Checkout cart={cart} setCart={setCart} onAuthClick={() => setIsAuthOpen(true)} isCartLoading={!!currentUser && !dbLoaded} />} />
          <Route path='/order-success' element={<OrderSuccess />} />
          <Route path='/cart' element={<Cart cart={cart} setCart={setCart} />} />
          <Route path='/profile' element={<Profile cart={cart} setCart={setCart} />} />
          <Route path='/inventory' element={<Inventory />} />
          <Route
            path="/products"
            element={
              <Products
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedSubcategory={selectedSubcategory}
                setSelectedSubcategory={setSelectedSubcategory}
                selectedSectionId={selectedSectionId}
                setSelectedSectionId={setSelectedSectionId}
                selectedSectionName={selectedSectionName}
                setSelectedSectionName={setSelectedSectionName}
                handleAddToCart={handleAddToCart}
                myList={myList}
                handleToggleMyList={handleToggleMyList}
                cartCount={cartCount}
                onCartClick={() => setIsCartOpen(true)}
                onAuthClick={() => setIsAuthOpen(true)}
              />
            }
          />
          <Route
            path="/product/:productId"
            element={
              <ProductDetails
                handleAddToCart={handleAddToCart}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                myList={myList}
                handleToggleMyList={handleToggleMyList}
                cartCount={cartCount}
                onCartClick={() => setIsCartOpen(true)}
                onAuthClick={() => setIsAuthOpen(true)}
              />
            }
          />
        </Routes>

        {/* ================= FOOTER ================= */}
        <div className="print:hidden">
          <Footer />
        </div>

        {/* ================= CART MODAL ================= */}
        <CartModal
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          cart={cart}
          setCart={setCart}
        />

        <AuthModal
          isOpen={isAuthOpen}
          onClose={() => setIsAuthOpen(false)}
        />
        <div className="print:hidden">
          <Toaster richColors position="bottom-right" />
          <QuotePopup />
        </div>
      </QuoteProvider>
      </UserSchemeProvider>
    </Router>
  );
}

export default App;